import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Table } from '@aws-northstar/ui/components'
import { Button, Icon, Link, Popover, StatusIndicator, Input } from '@cloudscape-design/components'

import useApplications from '../../hooks/query/useApplications'
import type { Application } from '../../types/applications'
import CONSTANTS from '../../constants'
import { getSortedFilteredApplications } from '../../helper/applicationList'
import { useGenericTagsManagementFormStore } from '../../stores/tags/genericTagsManagementFormStore'
import { useNewApplicationFormStore } from '../../stores/applications/newApplicationFormStore'

const envMap = [
	{label: "Develop", value: "dev"},
	{label: "Staging", value: "staging"},
	{label: "Production", value: "prod"},
]

const ApplicationOnboarding: FC = () => {
	const [filterText, setFilterText] = useState('')
	const navigate = useNavigate()

	const { data: applications, isLoading } = useApplications()

	const applicationCreationFormStore = useNewApplicationFormStore()
	const tagCreationFormStore = useGenericTagsManagementFormStore()	

	const filteredApplications = useMemo(
		() => getSortedFilteredApplications(applications || [], filterText),
		[applications, filterText]
	)

	const filterInput = (
		<Input
			placeholder="Search by ID or Application Name"
			value={filterText}
			onChange={({ detail }) => setFilterText(detail.value)}
		/>
	)

	const columnDefinitions = [
		// {
		// 	id: 'applicationID',
		// 	width: 100,
		// 	header: 'ID',
		// 	cell: (data: Application) => (
		// 		<Link onFollow={() => navigate(data?.applicationID ?? '')}>
		// 			{data?.applicationID ?? ''}
		// 		</Link>
		// 	),
		// 	sortingField: 'applicationID'
		// },
		{
			id: 'applicationName',
			width: 100,
			header: 'Application Name',
			cell: (data: Application) => (
				<Link onFollow={() => navigate(data?.applicationID ?? '')}>
					{`atlas-project-${data?.details?.applicationName || "-"}`}
				</Link>
			),
			sortingField: 'applicationName'
		},
		{
			id: 'environment',
			width: 100,
			header: 'Environment',
			cell: (data: Application) => {
				const details = data?.details
				if (!details) return ''
                
				// If additionalEnvName exists, use it
				if (details.additionalEnvName) return details.additionalEnvName
                
				// Safely check if environment is an object with a 'value', OR just a plain string
				const env = details.environment as any
				const normalizedEnvironmentValue = envMap.find((e) => e.value === env)?.label
				return normalizedEnvironmentValue
			},
			sortingField: 'environment'
		},
		{
			id: 'requestor',
			width: 100,
			header: 'Requested by',
			cell: (data: Application) => data?.requestorID ?? '',
			sortingField: 'requestorID'
		},
		{
			id: 'time',
			width: 100,
			header: 'Date Created',
			cell: (data: Application) => {
				if (data?.creationTime) {
					const date = new Date(data.creationTime)
					const localDate = date.toLocaleString("en-US", 
						{ 
						timeZone: "Asia/Singapore",
						weekday: "short",
						month: "numeric",
						day: "numeric",
						year: "numeric",
						hour: "numeric",
						minute: "numeric"
						}
					)
				
					return localDate
				}
				return ''
			},
			sortingField: 'creationTime'
		},
		{
			id: 'status',
			width: 100,
			header: 'Status',
			cell: (data: Application) => {
				const status = data?.status
				switch (status) {
					case 'PENDING_APPROVAL':
						return (
							<StatusIndicator type="in-progress" colorOverride="blue">
								Pending Approval
							</StatusIndicator>
						)
					case 'APPROVED':
						return <StatusIndicator type="success">Approved</StatusIndicator>
					case 'REJECTED':
						return <StatusIndicator type="error">Rejected</StatusIndicator>
					default:
						return null
				}
			},
			sortingField: 'status'
		},
		{
			id: 'requestCertificate',
			width: 140,
			header: 'Request Certificate',
			cell: (data: Application) =>
				data?.status === 'APPROVED' && data?.applicationID ? (
					<Link
						onFollow={() =>
							navigate(`/applications/${data.applicationID}/gcpCerts/new`)
						}
					>
						Request Certificate
					</Link>
				) : (
					'—'
				)
		}
	]

	const tableActions = (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 8
			}}
		>
			<Button variant="primary" onClick={() => 
					{
						applicationCreationFormStore.actions.clearFormValues()
						applicationCreationFormStore.actions.setCurrentStep(0)
						tagCreationFormStore.actions.clearFormValues()
						navigate('/applications/new')
					}
				}
				>
				New Application Request
			</Button>
			<Popover
				fixedWidth
				position="right"
				triggerType="custom"
				content={
					<p>
						Click the following link for a step-by-step guide on application
						onboarding:
						<a
							href={`${CONSTANTS.COMPASS_BASEURL}/gcp/user-guide/asp/developer-enablement/application-onboarding/application_onboarding/`}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Plus"
							style={{
								marginLeft: '5px',
								marginTop: '25px'
							}}
						>
							LINK
						</a>
					</p>
				}
			>
				<Icon name="status-info" />
			</Popover>
		</div>
	)


	return (
		<Table
			header="Applications"
			columnDefinitions={columnDefinitions}
			items={filteredApplications}
			actions={tableActions}
			filter={filterInput}
			disableRowSelect
			sortingDisabled={false}
			loading={isLoading}
			trackBy="applicationID" 
		/>
	)
}

export default ApplicationOnboarding
